import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n';

const LANG_META = {
  en: { flag: '🇬🇧', name: 'English' },
  bg: { flag: '🇧🇬', name: 'Български' },
  de: { flag: '🇩🇪', name: 'Deutsch' },
  es: { flag: '🇪🇸', name: 'Español' },
  fr: { flag: '🇫🇷', name: 'Français' },
  it: { flag: '🇮🇹', name: 'Italiano' },
  nl: { flag: '🇳🇱', name: 'Nederlands' },
  pl: { flag: '🇵🇱', name: 'Polski' },
};

export default function LanguageSwitcher({ placement = 'down', compact = false, style }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentMeta = LANG_META[current] || LANG_META.en;

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const select = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  const menuPos = placement === 'up'
    ? { bottom: 'calc(100% + 4px)' }
    : { top: 'calc(100% + 4px)' };

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={s.trigger}
      >
        <span style={s.flag}>{currentMeta.flag}</span>
        {!compact && (
          <>
            <span style={s.code}>{current.toUpperCase()}</span>
            <span style={s.caret}>▾</span>
          </>
        )}
      </button>
      {open && (
        <ul role="listbox" style={{ ...s.menu, ...menuPos }}>
          {SUPPORTED_LANGUAGES.map((lng) => {
            const meta = LANG_META[lng];
            if (!meta) return null;
            const active = lng === current;
            return (
              <li
                key={lng}
                role="option"
                aria-selected={active}
                onClick={() => select(lng)}
                style={{ ...s.item, ...(active ? s.itemActive : {}) }}
              >
                <span style={s.flag}>{meta.flag}</span>
                <span style={s.name}>{meta.name}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const s = {
  trigger: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#1E293B', border: '1px solid #334155', borderRadius: 8,
    padding: '6px 10px', cursor: 'pointer', color: '#F8FAFC',
    fontSize: '0.85rem', fontFamily: 'inherit',
  },
  flag: { fontSize: '1.05rem', lineHeight: 1 },
  code: { fontWeight: 600, letterSpacing: '0.04em' },
  caret: { fontSize: '0.65rem', color: '#94A3B8' },
  menu: {
    position: 'absolute', right: 0,
    margin: 0, padding: 4, listStyle: 'none',
    background: '#0F172A', border: '1px solid #334155', borderRadius: 8,
    minWidth: 170, zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  item: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
    color: '#CBD5E1', fontSize: '0.88rem',
  },
  itemActive: {
    background: 'rgba(124,58,237,0.15)', color: '#F8FAFC', fontWeight: 600,
  },
  name: { flex: 1 },
};
